/* eslint-disable object-curly-newline */

import React, { useRef, useReducer, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import ThumbnailCarousel from 'components/ProductPage/ProductMediaCarousel/ThumbnailCarousel/ThumbnailCarousel';
import ProductMediaItem from 'components/ProductPage/ProductMediaCarousel/ProductMediaItem';
import ProductMediaOverlay from 'components/ProductPage/ProductMediaCarousel/ProductMediaOverlay';
import VideoIconOverlay from 'components/ProductPage/ProductMediaCarousel/VideoIconOverlay/VideoIconOverlay';
import Carousel from 'components/Carousel/Carousel';
import { mediaQueries, breakpoints, space } from 'style/config';
import { Grid, Box } from 'components/ui';
import Actions from 'actions/Actions';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import Loader from 'components/Loader/Loader';
import UI from 'utils/UI';
import { IMAGE_SIZES, SHADOW } from 'components/ProductPage/ProductMediaCarousel/constants';
import analyticsConstants from 'analytics/constants';
import altImageSwipeTracker from 'analytics/bindings/pages/product/altImageSwipeTracker';
import productUtils from 'utils/product';
import skuUtils from 'utils/Sku';
import imageUtils from 'utils/Image';
import helpersUtils from 'utils/Helpers';
import mediaUtils from 'utils/Media';
import PanZoom from 'components/PanZoom/PanZoom';
import { DebouncedResize } from 'constants/events';

const { deferTaskExecution } = helpersUtils;
const { Media } = mediaUtils;
const { getMediaItems } = productUtils;
const { getImageBadgeFromUrl } = imageUtils;
const {
    COMPONENT_TITLE: { ALT_IMAGE_CAROUSEL },
    MEDIA_TYPE
} = analyticsConstants;
const SHADOW_OFFSET = space[4];
const HERO_DOTS_AREA_HEIGHT = space[5];

const styles = {
    carouselWrapper: {
        position: 'relative'
    }
};

const getHeroBadge = (mediaItems, showPDPTopOfPageRedesign) => {
    if (!showPDPTopOfPageRedesign) {
        return null;
    }

    const src = skuUtils.getImgSrc(IMAGE_SIZES.ZOOM, mediaItems[0]?.media) || '';
    const queryStart = src.indexOf('?');

    return queryStart >= 0 ? getImageBadgeFromUrl(src.slice(queryStart + 1)) : null;
};

const CAROUSEL_INITIAL = {
    isFullWidth: null,
    enableZoom: false,
    imageSize: null,
    isZoomLoopOpen: false,
    zoomLoopImage: null
};

function mediaCarouselReducer(state, action) {
    switch (action.type) {
        case 'MOUNT_INIT':
            return { ...state, isFullWidth: action.isFullWidth, imageSize: action.imageSize };
        case 'RESIZE_FULL':
            return { ...state, isFullWidth: action.isFullWidth, imageSize: action.imageSize };
        case 'SET_ZOOM':
            return { ...state, enableZoom: action.enableZoom };
        case 'MOVE_ZOOM':
            return { ...state, isZoomLoopOpen: true, zoomLoopImage: action.media };
        default:
            return state;
    }
}

const isSmallViewport = () => window.matchMedia(breakpoints.smMax).matches;

// eslint-disable-next-line complexity
const ProductMediaCarousel = ({ sku, preventPageRenderReport, product, showPDPTopOfPageRedesign, showPDPVideoIcon }) => {
    const dispatch = useDispatch();
    const [state, localDispatch] = useReducer(mediaCarouselReducer, CAROUSEL_INITIAL);

    const carouselRef = useRef(null);
    const thumbnailRef = useRef(null);
    const loopSizeRef = useRef(null);
    const heroHoverMask = useRef(null);
    const imageSizeDomRef = useRef(null);
    const heroHoverZoomRef = useRef(null);

    const isFullWidthRef = useRef(state.isFullWidth);
    isFullWidthRef.current = state.isFullWidth;
    const enableZoomRef = useRef(state.enableZoom);
    enableZoomRef.current = state.enableZoom;
    const imageSizeStateRef = useRef(state.imageSize);
    imageSizeStateRef.current = state.imageSize;

    const mediaItems = useMemo(() => getMediaItems(product), [product]);
    const mediaItemsRef = useRef(mediaItems);
    mediaItemsRef.current = mediaItems;

    const hasSeenFirstScrollEvent = useRef(false);
    const activeMediaIndex = useRef(0);

    const getGridMaxWidth = () => IMAGE_SIZES.LARGE + 1;

    const getImageSize = () => imageSizeDomRef.current?.getBoundingClientRect().width - 1;

    const getLoopSize = () => loopSizeRef.current?.offsetWidth;

    const getLoopThumbSize = () => (getLoopSize() * getImageSize()) / IMAGE_SIZES.ZOOM;

    const getLoopCoords = (x, y) => {
        if (state.isZoomLoopOpen) {
            const imgSize = imageSizeStateRef.current;
            const loopThumbSize = getLoopThumbSize();
            const smallX = x - loopThumbSize / 2;
            const smallY = y - loopThumbSize / 2;

            return {
                x: -((smallX / imgSize) * IMAGE_SIZES.ZOOM - imgSize),
                y: -((smallY / imgSize) * IMAGE_SIZES.ZOOM - imgSize)
            };
        }

        return { x: 0, y: 0 };
    };

    const renderSvgHeroImageMask = coordinates => {
        const loopThumbSize = getLoopThumbSize();
        const imageSize = getImageSize();
        const minCoord = loopThumbSize / 2;
        const maxCoord = imageSize - loopThumbSize / 2;
        let { x, y } = coordinates;
        x = x < minCoord ? minCoord : x > maxCoord ? maxCoord : x;
        y = y < minCoord ? minCoord : y > maxCoord ? maxCoord : y;

        const svgMarkup = `<svg style='position: absolute'>
            <defs>
                <mask id='heroHoverMediaMask'>
                    <rect
                        x='0' y='0'
                        width=${imageSizeStateRef.current}
                        height=${imageSizeStateRef.current}
                        fill='#4d4d4d' />
                    <rect
                        x=${x - loopThumbSize / 2}
                        y=${y - loopThumbSize / 2}
                        width=${loopThumbSize}
                        height=${loopThumbSize}
                        fill='white' stroke='black' />
                </mask>
            </defs>
        </svg>`;
        UI.requestFrame(() => {
            if (heroHoverMask.current) {
                heroHoverMask.current.innerHTML = svgMarkup;
            }

            heroHoverZoomRef.current?.updateCoords(getLoopCoords(x, y));
        });
    };

    const moveZoom = (coordinates, index, items) => {
        if (!isFullWidthRef.current) {
            const { type, media } = items[index] || {};

            if (type === 'IMAGE') {
                localDispatch({ type: 'MOVE_ZOOM', media });
                renderSvgHeroImageMask(coordinates);
            }
        }
    };

    const handleZoom = (enableZoom, event) => {
        const zoomStateHasChanged = enableZoomRef.current !== enableZoom;

        if (!isFullWidthRef.current && zoomStateHasChanged) {
            const simulateMouseMoveEvent = !enableZoomRef.current && enableZoom;

            if (simulateMouseMoveEvent) {
                carouselRef.current?.mouseMove(event);
            }

            localDispatch({ type: 'SET_ZOOM', enableZoom });
        }
    };

    const handleOnScroll = index => {
        thumbnailRef.current?.scrollTo(index);

        if (!hasSeenFirstScrollEvent.current) {
            hasSeenFirstScrollEvent.current = true;
            activeMediaIndex.current = index;

            return;
        }

        if (index === activeMediaIndex.current) {
            return;
        }

        activeMediaIndex.current = index;

        if (showPDPTopOfPageRedesign) {
            altImageSwipeTracker.fireOnce();
        }
    };

    const handleOnMouseMove = (cor, index) => {
        moveZoom(cor, index, mediaItemsRef.current);
    };

    const launchProductZoomModal = (index, autoFullscreen = false) => {
        return () => {
            const action = Actions.showProductMediaZoomModal(
                true,
                product,
                index,
                mediaItemsRef.current,
                false,
                showPDPTopOfPageRedesign,
                autoFullscreen
            );
            deferTaskExecution(() => dispatch(action));
        };
    };

    useEffect(() => {
        altImageSwipeTracker.reset();
        const initialImageSize = getImageSize();
        localDispatch({
            type: 'MOUNT_INIT',
            isFullWidth: isSmallViewport(),
            imageSize: initialImageSize
        });

        const handleResize = () => {
            const smallView = isSmallViewport();
            const currentIsFullWidth = isFullWidthRef.current;
            const isFullWidthChanged = smallView !== currentIsFullWidth;

            if (isFullWidthChanged) {
                localDispatch({
                    type: 'RESIZE_FULL',
                    isFullWidth: smallView,
                    imageSize: smallView ? imageSizeStateRef.current : getImageSize()
                });
            } else if (!smallView) {
                localDispatch({
                    type: 'RESIZE_FULL',
                    isFullWidth: false,
                    imageSize: getImageSize()
                });
            }
        };

        window.addEventListener(DebouncedResize, handleResize);

        return () => {
            altImageSwipeTracker.reset();
            window.removeEventListener(DebouncedResize, handleResize);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        hasSeenFirstScrollEvent.current = false;
        activeMediaIndex.current = 0;
        const maxValidIndex = mediaItems.length - 1;
        const index = carouselRef.current?.getCurrentIndex();

        if (index > maxValidIndex) {
            carouselRef.current?.scrollTo(0);
        }
    }, [sku.skuId, mediaItems]);

    const { isFullWidth, enableZoom, isZoomLoopOpen, zoomLoopImage } = state;
    const isHeroMediaDotsEnabled = isFullWidth && showPDPTopOfPageRedesign;
    const isSingleItem = mediaItems.length === 1;
    const isRedesignedFullWidth = isFullWidth && showPDPTopOfPageRedesign;
    const heroBadge = getHeroBadge(mediaItems, showPDPTopOfPageRedesign);
    const { firstVideoIndex, firstVideoThumbnail } = useMemo(() => {
        if (!showPDPVideoIcon) {
            return { firstVideoIndex: -1, firstVideoThumbnail: null };
        }

        const idx = mediaItems.findIndex(item => item.type === MEDIA_TYPE.VIDEO);

        return {
            firstVideoIndex: idx,
            firstVideoThumbnail: idx !== -1 ? mediaItems[idx].media.startImagePath : null
        };
    }, [mediaItems, showPDPVideoIcon]);

    const productMediaItems = mediaItems.map((item, index) => {
        const key = `productMediaItem_${sku.skuId}_${index}`;
        const sharedProps = {
            maskSize: state.imageSize,
            item,
            index,
            isFullWidth,
            isSingleItem,
            preventPageRenderReport,
            enableZoom,
            onClick: launchProductZoomModal,
            skuId: sku.skuId,
            showPDPTopOfPageRedesign
        };

        if (index === 0 && showPDPVideoIcon && firstVideoIndex !== -1) {
            return (
                <Box
                    key={key}
                    css={styles.carouselWrapper}
                >
                    <ProductMediaItem {...sharedProps} />
                    <Media lessThan='md'>
                        <VideoIconOverlay
                            onClick={launchProductZoomModal(firstVideoIndex, true)}
                            thumbnailSrc={firstVideoThumbnail}
                        />
                    </Media>
                </Box>
            );
        }

        return (
            <ProductMediaItem
                key={key}
                {...sharedProps}
            />
        );
    });

    return (
        <React.Fragment>
            <Grid
                id='product-media'
                alignItems={[null, null, 'flex-start']}
                justifyContent={[null, null, 'space-between']}
                gap={[null, null, 6]}
                columns={[null, null, `minmax(40px, 56px) minmax(360px, ${getGridMaxWidth()}px)`]}
            >
                <div>
                    <div ref={imageSizeDomRef} />
                    {state.isFullWidth !== null ? (
                        <Box
                            marginBottom={isFullWidth ? 2 : 0}
                            css={isRedesignedFullWidth ? styles.carouselWrapper : null}
                        >
                            <Carousel
                                ref={carouselRef}
                                shouldCenterItems={true}
                                {...(isFullWidth
                                    ? isRedesignedFullWidth
                                        ? {
                                            itemWidth: '100%',
                                            marginX: '-container',
                                            paddingBottom: HERO_DOTS_AREA_HEIGHT
                                        }
                                        : {
                                            paddingY: SHADOW_OFFSET,
                                            itemWidth: IMAGE_SIZES.SMALL,
                                            gap: 2,
                                            marginX: '-container',
                                            scrollPadding: 2
                                        }
                                    : {
                                        arrowVariant: 'simple',
                                        showArrowOnHover: false
                                    })}
                                dotsShown={isFullWidth ? (showPDPTopOfPageRedesign ? 6 : 5) : null}
                                isHeroMediaDots={isHeroMediaDotsEnabled}
                                dotTypes={isHeroMediaDotsEnabled ? mediaItems.map(item => item.type) : null}
                                dotsStyle={
                                    isHeroMediaDotsEnabled
                                        ? {
                                            bottom: 0,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            height: HERO_DOTS_AREA_HEIGHT,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }
                                        : {
                                            top: SHADOW_OFFSET + space[2],
                                            left: IMAGE_SIZES.SMALL,
                                            transform: 'translateX(-100%)'
                                        }
                                }
                                onMouseEnter={handleZoom}
                                onMouseLeave={handleZoom}
                                onMouseMove={handleOnMouseMove}
                                onScroll={handleOnScroll}
                                items={productMediaItems}
                                title={ALT_IMAGE_CAROUSEL}
                                sku={sku}
                            />
                            {isRedesignedFullWidth && (
                                <ProductMediaOverlay
                                    product={product}
                                    sku={sku}
                                />
                            )}
                        </Box>
                    ) : (
                        <ProductImage
                            id={sku.skuId}
                            hideBadge={!showPDPTopOfPageRedesign}
                            size={[IMAGE_SIZES.SMALL, null, IMAGE_SIZES.LARGE]}
                            width={[IMAGE_SIZES.SMALL, null, '100%']}
                            borderRadius={2}
                            {...(isSingleItem ? { marginX: 'auto' } : { marginLeft: [-2, null, 0], boxShadow: SHADOW })}
                            {...(showPDPTopOfPageRedesign && { skuImages: sku.skuImages, badge: heroBadge })}
                            isPageRenderImg={true}
                            disableLazyLoad={true}
                        />
                    )}
                </div>
                <div
                    css={{
                        display: 'none',
                        [mediaQueries.md]: {
                            display: 'block',
                            order: -1
                        }
                    }}
                >
                    {isSingleItem || (
                        <ThumbnailCarousel
                            productId={product.productDetails?.productId}
                            ref={thumbnailRef}
                            carouselRef={carouselRef}
                            seeAllClick={launchProductZoomModal}
                            items={productMediaItems}
                        />
                    )}
                </div>
            </Grid>
            <div
                ref={loopSizeRef}
                style={{ position: 'absolute', width: '50%' }}
            />
            {enableZoom && (
                <React.Fragment>
                    <Box
                        backgroundColor='white'
                        position='absolute'
                        zIndex='modal'
                        right={0}
                        top={-7}
                        width='50%'
                        overflow='hidden'
                        boxShadow='medium'
                    >
                        <div css={{ paddingBottom: '100%' }} />
                        <Loader isShown={true} />
                        <div
                            css={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%'
                            }}
                        >
                            <PanZoom
                                ref={zoom => {
                                    heroHoverZoomRef.current = zoom;
                                }}
                                width='100%'
                                height='100%'
                                disableButtons={true}
                                scale={IMAGE_SIZES.ZOOM / getLoopSize()}
                            >
                                {isZoomLoopOpen && (
                                    <ProductImage
                                        src={skuUtils.getImgSrc(IMAGE_SIZES.ZOOM, zoomLoopImage)}
                                        hideBadge={true}
                                        size={IMAGE_SIZES.ZOOM}
                                    />
                                )}
                            </PanZoom>
                        </div>
                    </Box>
                    <div ref={heroHoverMask} />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

ProductMediaCarousel.shouldUpdatePropsOn = ['product.currentSku', 'product.hoveredSku'];

export default wrapFunctionalComponent(ProductMediaCarousel, 'ProductMediaCarousel');
